import React, { useContext, useEffect, useMemo, useCallback, useState } from 'react';
import { policyTypes } from '@ourbranch/policy-types';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { isDraftablePolicyType } from 'core/helpers/policy-drafting';
import { isPast, isToday, subDays, startOfDay } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { getPolicyStatus, PolicyStatus, isImportedPolicy } from 'core/helpers/policy-status';
import { appConfig } from 'brand-config';
import { removeSpecificQueryParam } from 'core/helpers/url-utils';
import { track } from 'core/helpers/analytics';
import { AuthContext } from 'core/components/auth';
import { ValidationErrorsModal, useValidationErrorsModal } from 'core/components/validation-errors-modal';
import Field from 'core/components/form/form.v2';
import { isCondensedLayout } from 'core/components/theme/condensed-layout';
import { CognitoPermissionGroups } from 'core/helpers/cognito-permission-groups';
import { get, getUmbrellaSuffix, policyTypes as policyTypeOptions } from 'core/helpers/lookups';
import { ReadOnlyProvider } from 'common/disabled-context';
import { useStore } from 'core/store';
import { PolicyModalTypes } from 'core/store/account-store/policies-store/policy-store/policy-store';
import { STAFF_AUTO_MAX_BACKDATE_DAYS } from '../dates';
import Footer from '../footer';
import { getNewChangeDateConflict } from './helpers';
import { SegmentFormLeft, SegmentFormRight } from '../segment-form';
import { HomePolicy } from './home-policy';
import { CondoPolicy } from './condo-policy';
import { AutoPolicy } from './auto-policy';
import { RentersPolicy } from './renters-policy';
import { FarmPolicy } from './farm-policy';
import PreviewFooter from '../footer/preview-footer';
import { MakeAChangeDrawer, DRAWER_PHASES } from './make-a-change-drawer';
import ChangePicker from './make-a-change-drawer/change-picker';
import { getChangeType, getChangeTypes } from './make-a-change-drawer/change-types';
import { getChangesOutsideType, getDrawerErrors } from './make-a-change-drawer/change-scope';
import { DrawerErrors, OutsideChangesWarning } from './make-a-change-drawer/drawer-notices';
import { getOpenPolicies, getPolicyTabLabel } from 'customer/components/workspace/workspace-summary';
import PricePanel from './make-a-change-drawer/price-change-panel';
import NoteForTheFile from './make-a-change-drawer/note-for-the-file';
import { internalNoteSchema } from '../preview/preview.validation';

import useStyles from './details.styles';

const getPolicyTypeLabel = get(policyTypeOptions);

// The drawer's labels for today's footer choices.
const DRAWER_CHANGE_TYPE_LABELS = {
  new_change: 'Create a new change',
  edit_change: 'Edit the selected change'
};

const DetailsForm = observer(function DetailsForm({
  loadingPreview,
  handleRadioChange,
  state,
  handleStartDate,
  repEmail,
  preview: pagePreview
}) {
  const { touched, values, handleSubmit, errors, validateForm, setErrors, setTouched } = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();
  const history = useHistory();

  const { account } = useStore();
  const {
    policies: {
      list: accountPolicies,
      policy: policyStore,
      policy: {
        segment,
        policy,
        preview,
        changed: changedStatus,
        setChanged,
        loading,
        setActivePolicyModal,
        setScrollTo,
        makeChangeOpen,
        setMakeChangeOpen,
        makeChangeNextPolicyId,
        setMakeChangeNextPolicyId,
        setMakeChangeNote
      }
    }
  } = account;
  // Condensed: Details is read-only; the Make a change drawer holds the form and today's footer controls.
  const condensed = isCondensedLayout();

  const session = useContext(AuthContext);
  const classes = useStyles();
  const changed = useMemo(() => session?.canEditPolicies && Object.keys(touched).length > 0, [touched, session]);
  const editingFirstSegment =
    policy.segments[0].segmentId === segment.segmentId && state.get('changeType') === 'edit_change';

  const policyStatus = loading ? null : getPolicyStatus(policy);
  const cancelled = policyStatus === PolicyStatus.InCancellation || policyStatus === PolicyStatus.Cancelled;
  // #36833: no "Underwriting Changes for Renewal" on an original imported term where the brand blocks it
  // (CFM). `isImportedPolicy` reads the `source` marker, which renewals drop, so only the imported term
  // itself is blocked. policy-mod refuses the change too.
  const renewalChangesBlocked =
    !!appConfig?.featureFlags?.blockRenewalChangesOnImportedTerms && isImportedPolicy(policy);

  // Reactively flag a "Create New Change" effective date that collides with an existing
  // segment, so the date picker can show inline guidance up front (#31068). The agent can
  // still preview; the backend rejection is then caught and surfaced via the neutral
  // WarningModal. Edit path keeps its own handling.
  const startDateConflict =
    state.get('changeType') === 'new_change' && state.get('startDate')
      ? getNewChangeDateConflict({ policy, startDate: state.get('startDate') })
      : null;

  const footerOptions = () => {
    const options = [{ value: 'new_change', label: 'Create New Change' }];
    const segmentDate = toDate(segment.startDate);
    // Past auto segments within the 30-day backdate window remain editable for non-canBackDate users —
    // disallowed change types are caught by the submission-time validator in details.jsx.
    const isAutoWithinBackdateWindow =
      policy.policyType?.includes(policyTypes.Auto) &&
      segmentDate >= startOfDay(subDays(new Date(), STAFF_AUTO_MAX_BACKDATE_DAYS));
    if (isPast(segmentDate) && !session?.canBackDate && !isToday(segmentDate) && !isAutoWithinBackdateWindow) {
      return options;
    }
    options.push({ value: 'edit_change', label: 'Edit Selected Change' });
    return options;
  };

  useEffect(() => {
    if (changed && !changedStatus) {
      setChanged(true);
    } else if (!changed) {
      setChanged(false);
    }
  }, [changed, changedStatus, setChanged]);

  useEffect(() => {
    if (!condensed) {
      return undefined;
    }
    // Picking another policy in the drawer keeps it open on that policy's tab.
    if (makeChangeNextPolicyId === policy.id) {
      setMakeChangeNextPolicyId(null);
    }
    return () => {
      if (!policyStore.makeChangeNextPolicyId) {
        setMakeChangeOpen(false);
        setMakeChangeNote('');
      }
    };
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length) {
      // logging validation errors on details pane for better visbility of production issues
      console.log({ errors });
    }
  }, [errors]);

  // Resolves true when the form was valid and submitted. The condensed drawer lists errors itself.
  const onSubmit = useCallback(
    async ({ showErrorsModal = true } = {}) => {
      const errors = await validateForm();

      if (Object.keys(errors).length) {
        setErrors(errors);
        setTouched(errors);
        setShowValidationErrorsModal(showErrorsModal);
        return false;
      }
      handleSubmit();
      return true;
    },
    [handleSubmit, setShowValidationErrorsModal, validateForm, setErrors, setTouched]
  );

  // Condensed drawer: the page preview it last asked for supersedes this one (undefined = not asked). A new
  // preview has a new id, so the previous one never shows while the next is on its way.
  const [drawerPreviewAfter, setDrawerPreviewAfter] = useState(undefined);
  let drawerPhase = DRAWER_PHASES.EDIT;
  if (loadingPreview) {
    drawerPhase = DRAWER_PHASES.LOADING;
  } else if (drawerPreviewAfter !== undefined && pagePreview?.id && pagePreview.id !== drawerPreviewAfter) {
    drawerPhase = DRAWER_PHASES.PREVIEW;
  }
  // "What's changing?": with one choice (Something else) it's chosen already.
  const changeTypes = getChangeTypes(policy.policyType);
  const [changeTypeValue, setChangeTypeValue] = useState(null);
  const changeType = changeTypes.length === 1 ? changeTypes[0] : getChangeType(policy.policyType, changeTypeValue);
  const [showDrawerErrors, setShowDrawerErrors] = useState(false);
  const [noteError, setNoteError] = useState(null);
  const drawerErrors = showDrawerErrors ? getDrawerErrors(errors, values, changeType) : [];
  const changesOutsideType =
    drawerPhase === DRAWER_PHASES.PREVIEW ? getChangesOutsideType(pagePreview.diff, changeType) : [];
  // The customer's other open policies; picking one moves to its tab with the drawer open.
  const changeablePolicies = getOpenPolicies(accountPolicies)
    .filter((accountPolicy) => getPolicyStatus(accountPolicy) !== PolicyStatus.InCancellation)
    .map((accountPolicy) => ({ id: accountPolicy.id, label: getPolicyTabLabel(accountPolicy) }));

  const onDrawerPreview = async () => {
    const previousPreviewId = pagePreview?.id ?? null;
    if (await onSubmit({ showErrorsModal: false })) {
      setShowDrawerErrors(false);
      setDrawerPreviewAfter(previousPreviewId);
    } else {
      setShowDrawerErrors(true);
    }
  };
  const closeDrawer = () => {
    setDrawerPreviewAfter(undefined);
    setChangeTypeValue(null);
    setShowDrawerErrors(false);
    setMakeChangeNextPolicyId(null);
    setMakeChangeNote('');
    setNoteError(null);
    setMakeChangeOpen(false);
  };
  // The modal checks the note again on save; checking here first keeps the agent in the drawer to fix it.
  const reviewAndSave = async () => {
    try {
      // Read here rather than in render, so typing the note doesn't re-render the form.
      await internalNoteSchema.validate(policyStore.makeChangeNote);
    } catch (noteValidationError) {
      setNoteError(noteValidationError.message);
      return;
    }
    setNoteError(null);
    setActivePolicyModal(PolicyModalTypes.PREVIEW);
  };
  const switchPolicy = (policyId) => {
    if (policyId !== policy.id) {
      setMakeChangeNextPolicyId(policyId);
      history.push(`/customer/${account.id}/policy/${policyId}`);
    }
  };

  useEffect(() => {
    if (Object.keys(touched).length && preview?.id && !isDraftablePolicyType(policy.policyType)) {
      removeSpecificQueryParam(history, 'previewId');
    }
  }, [touched, preview?.id]);

  // Validation Errors modal "go to section" (#30230). Policy details is a single page (no tabs),
  // so just scroll to the anchor (setScrollTo drives useScroll → scroll + flash) and close the
  // modal. `target` is the resolved nav target from the modal ({ anchor }).
  const handleGoToSection = ({ anchor }) => {
    track('Staff Validation Error Go To Section Clicked', { section: anchor, flow: 'policy' });
    setScrollTo(anchor);
    setShowValidationErrorsModal(false);
  };

  // `show` limits Home and Auto to the drawer's chosen change; null is the full form.
  const renderSections = (show = null) => (
    <>
      {/* need policy to know if we are on a bix policy in a nested component */}
      {policy.policyType === policyTypes.Auto && <AutoPolicy show={show} />}
      {policy.policyType === policyTypes.Home && <HomePolicy repEmail={repEmail} show={show} />}
      {policy.policyType === policyTypes.Condo && <CondoPolicy />}
      {policy.policyType === policyTypes.Renters && <RentersPolicy />}
      {isFarmBasedPolicyType(policy.policyType) && <FarmPolicy />}
    </>
  );
  const sections = renderSections();

  const changeTypeOptions = footerOptions();
  const segmentFormRightProps = {
    editingFirstSegment,
    isEditingSegment: state.get('changeType') === 'edit_change',
    onChange: handleStartDate,
    startDate: state.get('startDate'),
    minDate: state.get('minDate'),
    maxDate: state.get('maxDate'),
    error: startDateConflict,
    policy,
    renewalChangesBlocked
  };

  const previewFooter = preview?.id && !cancelled && !session.viewOnly && (
    <PreviewFooter
      onSubmit={async () => {
        if (isDraftablePolicyType(policy.policyType) && changed) {
          await onSubmit();
          return;
        }

        setActivePolicyModal(PolicyModalTypes.PREVIEW);
      }}
      policyPreview={preview}
    />
  );

  const validationErrorsModal = (
    <ValidationErrorsModal
      open={showValidationErrorsModal}
      onClose={() => setShowValidationErrorsModal(false)}
      onGoToSection={handleGoToSection}
    />
  );

  if (condensed) {
    // The sections render once: read-only on the page, or editable in the drawer. Two live copies would share
    // field ids, so a label in the drawer could focus the hidden copy.
    return (
      <div>
        {!makeChangeOpen && <ReadOnlyProvider hideEditControls>{sections}</ReadOnlyProvider>}
        <MakeAChangeDrawer
          open={makeChangeOpen}
          eyebrow={`${account.fname} ${account.lname} · ${getPolicyTypeLabel(policy.policyType)}${getUmbrellaSuffix(policyStore)} policy`}
          changed={changed}
          phase={drawerPhase}
          previewPanel={drawerPhase === DRAWER_PHASES.PREVIEW && <PricePanel preview={pagePreview} />}
          canPreview={!!changeType}
          onPreview={onDrawerPreview}
          onEdit={() => setDrawerPreviewAfter(undefined)}
          onReviewAndSave={reviewAndSave}
          onClose={closeDrawer}
        >
          {/* What was previewed stays put until Edit. */}
          <ReadOnlyProvider readOnly={drawerPhase !== DRAWER_PHASES.EDIT}>
            <ChangePicker
              policies={changeablePolicies}
              policyId={policy.id}
              onPolicyChange={switchPolicy}
              changeTypes={changeTypes}
              changeType={changeType?.value}
              onChangeTypeChange={(value) => {
                // A new choice starts over from editing, as in the prototype.
                setChangeTypeValue(value);
                setDrawerPreviewAfter(undefined);
              }}
            />
            {changesOutsideType.length > 0 && <OutsideChangesWarning fields={changesOutsideType} />}
            {drawerErrors.length > 0 && <DrawerErrors errors={drawerErrors} />}
            {changeType && renderSections(changeType.sections)}
          </ReadOnlyProvider>
          <div
            className={classNames(classes.drawerStart, {
              [classes.hidden]: drawerPhase === DRAWER_PHASES.PREVIEW || !changeType
            })}
          >
            <SegmentFormLeft
              onRadioChange={handleRadioChange}
              options={changeTypeOptions.map((option) => ({
                ...option,
                label: DRAWER_CHANGE_TYPE_LABELS[option.value]
              }))}
              value={state.get('changeType')}
            />
            <SegmentFormRight {...segmentFormRightProps} mode={null} />
            {session.canDelayPaymentOrRefund && (
              <Field
                type="checkbox"
                id="skipImmediateBillOrRefund"
                name="skipImmediateBillOrRefund"
                label="Delay payment or refund"
                ignoreGlobalDisabledState
                permissions={{
                  isLicensedAction: false,
                  edit: { groups: [CognitoPermissionGroups.canDelayPaymentOrRefund] }
                }}
              />
            )}
          </div>
          {changeType && <NoteForTheFile error={noteError} onEdit={() => setNoteError(null)} />}
        </MakeAChangeDrawer>
        {previewFooter}
        {validationErrorsModal}
      </div>
    );
  }

  return (
    <div className={changed ? classes.changed : null}>
      {sections}

      {changed && !session.viewOnly && !cancelled && !preview?.id && policy.canEndorse !== false && (
        <Footer
          loading={loadingPreview}
          leftForm={
            <SegmentFormLeft
              onRadioChange={handleRadioChange}
              options={changeTypeOptions}
              value={state.get('changeType')}
            />
          }
          form={<SegmentFormRight {...segmentFormRightProps} />}
          onClick={onSubmit}
          policy={policy}
        />
      )}
      {previewFooter}
      {validationErrorsModal}
    </div>
  );
});

DetailsForm.propTypes = {
  loadingPreview: PropTypes.bool.isRequired,
  handleRadioChange: PropTypes.func.isRequired,
  state: PropTypes.object.isRequired,
  handleStartDate: PropTypes.func.isRequired,
  repEmail: PropTypes.string.isRequired,
  preview: PropTypes.object
};

DetailsForm.defaultProps = {
  preview: null
};

export default DetailsForm;
